import {
    Assessment,
    AutoAwesome,
    EmojiEvents,
    EmojiObjects,
    Psychology,
    School,
    Verified,
    Work,
    WorkOutline,
} from '@mui/icons-material';
import { Avatar, alpha, Box, Card, CardContent, Chip, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
    ICertificateSelectionUIData,
    IRoleSelectionUIData,
    ISkillExtractionUIData,
    ISuperPowersData,
} from '@sharedInterfaces/IEmployeeTalkState';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

const ETTResultsOverviewComponent: React.FC<ITemplateComponentProps> = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { getSelectedSkills, getSelectedCertificates, getSelectedRoles, getSelectedSuperPowers } = useEmployeeTalk();
    const employee = useSelector((state: AppState) => state.employee);

    const selectedSkills = getSelectedSkills();
    const selectedCertificates = getSelectedCertificates();
    const selectedRoles = getSelectedRoles();
    const selectedSuperPowers = getSelectedSuperPowers();

    const stats = [
        {
            label: 'Skills',
            value: selectedSkills.length,
            icon: <Assessment />,
            color: theme.palette.primary.main,
        },
        {
            label: 'SuperPowers',
            value: selectedSuperPowers.length,
            icon: <EmojiEvents />,
            color: theme.palette.warning.main,
        },
        {
            label: 'Zertifikate',
            value: selectedCertificates.length,
            icon: <Verified />,
            color: theme.palette.success.main,
        },
        {
            label: 'Rollen',
            value: selectedRoles.length,
            icon: <Work />,
            color: theme.palette.info.main,
        },
    ];

    const topSkills = selectedSuperPowers.slice(0, 6);
    const hasNoSkills = selectedSkills.length === 0;

    const renderViewContent = () => (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: isMobile ? 1 : 0 }}>
            {/* Section title is shown by ETTBaseTemplateComponent, no need to duplicate */}

            {/* Warning when no skills selected */}
            {hasNoSkills && (
                <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        border: `2px solid ${theme.palette.warning.main}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 48, height: 48 }}>
                                <Psychology />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.dark }}>
                                    Keine Skills ausgewählt
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Bitte gehen Sie zurück zur Skills-Seite und wählen Sie mindestens einige Ihrer
                                    Fähigkeiten aus.
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {/* Personal Info Card */}
                <Box
                    sx={{
                        flex: { xs: '1 1 100%', md: '1 1 300px' },
                        minWidth: isMobile ? 'auto' : 300,
                        order: isMobile ? 1 : 0,
                    }}
                >
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Avatar
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        bgcolor: theme.palette.primary.main,
                                        fontSize: '1.5rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {employee?.firstName?.charAt(0)}
                                    {employee?.lastName?.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {employee?.firstName} {employee?.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Talent-Profil
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6 }}>
                                <FormatedTextInput value={employee?.description || 'Keine Beschreibung vorhanden'} />
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<Psychology />}
                                    label={`${stats[0].value} Skills`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<EmojiObjects />}
                                    label={`${stats[1].value} SuperPowers`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<School />}
                                    label={`${stats[2].value} Zertifikate`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<WorkOutline />}
                                    label={`${stats[3].value} Rollen`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Super Powers Card */}
                <Box
                    sx={{
                        flex: { xs: '1 1 100%', md: '2 1 500px' },
                        minWidth: isMobile ? 'auto' : 500,
                        order: isMobile ? 2 : 1,
                    }}
                >
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            border: `2px solid ${theme.palette.warning.main}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
                            position: 'relative',
                            overflow: 'visible',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -12,
                                left: 24,
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                background: `linear-gradient(45deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.4)}`,
                            }}
                        >
                            <AutoAwesome sx={{ fontSize: 16 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                SUPERPOWERS
                            </Typography>
                        </Box>
                        <CardContent sx={{ p: 3, pt: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.warning.dark }}>
                                Ihre herausragenden Skills
                            </Typography>
                            {topSkills.length > 0 ? (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {topSkills.map((skill) => (
                                        <Chip
                                            key={skill}
                                            label={skill}
                                            sx={{
                                                backgroundColor: theme.palette.warning.main,
                                                color: 'white',
                                                fontWeight: 600,
                                            }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Keine SuperPowers ausgewählt
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
                {/* All Skills */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: isMobile ? 'auto' : 400 }}>
                    <Box
                        sx={{
                            height: '100%',
                            p: isMobile ? 2 : 3,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile ? 2 : 3 }}>
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    width: isMobile ? 28 : 40,
                                    height: isMobile ? 28 : 40,
                                }}
                            >
                                <Psychology sx={{ fontSize: isMobile ? 16 : 24 }} />
                            </Avatar>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : undefined }}
                            >
                                Alle Skills ({selectedSkills.length})
                            </Typography>
                        </Box>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {selectedSkills.length > 0 ? (
                                selectedSkills.map((skill, index) => (
                                    <Box key={skill.title}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: 1,
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 500, fontSize: isMobile ? '0.85rem' : undefined }}
                                                >
                                                    {skill.title}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                                                >
                                                    {skill.category}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={
                                                    skill.level === 1
                                                        ? 'Junior'
                                                        : skill.level === 2
                                                          ? 'Mid-Level'
                                                          : skill.level === 3
                                                            ? 'Senior'
                                                            : 'Expert'
                                                }
                                                size="small"
                                                sx={{
                                                    backgroundColor:
                                                        skill.level === 1
                                                            ? '#90caf9'
                                                            : skill.level === 2
                                                              ? '#66bb6a'
                                                              : skill.level === 3
                                                                ? '#ffa726'
                                                                : '#ef5350',
                                                    color: skill.level >= 3 ? '#ffffff' : '#1a1a1a',
                                                }}
                                            />
                                        </Box>
                                        {index < selectedSkills.length - 1 && (
                                            <Box
                                                component="hr"
                                                sx={{
                                                    border: 'none',
                                                    borderTop: `1px solid ${theme.palette.divider}`,
                                                    my: 1,
                                                }}
                                            />
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Keine Skills ausgewählt
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Certificates */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: isMobile ? 'auto' : 400 }}>
                    <Box
                        sx={{
                            height: '100%',
                            p: isMobile ? 2 : 3,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile ? 2 : 3 }}>
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.info.main,
                                    width: isMobile ? 28 : 40,
                                    height: isMobile ? 28 : 40,
                                }}
                            >
                                <School sx={{ fontSize: isMobile ? 16 : 24 }} />
                            </Avatar>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : undefined }}
                            >
                                Zertifikate ({selectedCertificates.length})
                            </Typography>
                        </Box>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {selectedCertificates.length > 0 ? (
                                selectedCertificates.map((cert, index) => (
                                    <Box key={cert.title}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                }}
                                            >
                                                <School />
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 500, fontSize: isMobile ? '0.85rem' : undefined }}
                                                >
                                                    {cert.title}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                                                >
                                                    {cert.issuer || 'Zertifikat'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {index < selectedCertificates.length - 1 && (
                                            <Box
                                                component="hr"
                                                sx={{
                                                    border: 'none',
                                                    borderTop: `1px solid ${theme.palette.divider}`,
                                                    my: 1,
                                                }}
                                            />
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Keine Zertifikate ausgewählt
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Roles */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: isMobile ? 'auto' : 400 }}>
                    <Box
                        sx={{
                            height: '100%',
                            p: isMobile ? 2 : 3,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile ? 2 : 3 }}>
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.success.main,
                                    width: isMobile ? 28 : 40,
                                    height: isMobile ? 28 : 40,
                                }}
                            >
                                <WorkOutline sx={{ fontSize: isMobile ? 16 : 24 }} />
                            </Avatar>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : undefined }}
                            >
                                Gewünschte Rollen ({selectedRoles.length})
                            </Typography>
                        </Box>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {selectedRoles.length > 0 ? (
                                selectedRoles.map((role, index) => (
                                    <Box
                                        key={role.title}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                            background: alpha(theme.palette.info.main, 0.05),
                                            mb: index < selectedRoles.length - 1 ? 2 : 0,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, fontSize: isMobile ? '0.85rem' : undefined }}
                                        >
                                            {role.title}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Keine Rollen ausgewählt
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    return <ETTBaseTemplateComponent {...props} renderViewContent={renderViewContent} />;
};

export default ETTResultsOverviewComponent;
